/*  
<summary>
  Functional Component: ProductList
  Developer: Ashutosh Modgil
  Created Date: 4-April-2025
  Description: Renders a Product management interface with features for viewing, adding, editing, deleting, and filtering staff. Includes pagination, popups for detailed views, and manual refresh and invalid staff toggles.
</summary>
<returns>JSX for the Product Management interface</returns>
*/

import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import noMessageIcon from "../../../assets/Images/svg/018-empty.svg";
import { useStore } from "../../../contexts/store-provider";
import {
  getDefaultActiveKeysProduct,
  resetCategoriesProducts,
} from "../../../helpers/category-helper";
import { getStoreIdFromLocalStorage } from "../../../helpers/localstorage-helper";
import { ICategoryProduct } from "../../../models/state/IStoreProduct";
import LoaderButton from "../../../shared-components/button/loader-button";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import LanguageSlider from "../../../shared-components/language-slider/language-slider";
import AddPopupHOC from "../../../shared-components/popup/add/add-popup-hoc";
import DeletePopup from "../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../shared-components/popup/delete/delete-popup-hoc";
import ShowPopupHOC from "../../../shared-components/popup/show/show-popup-hoc";
import { formatMessage } from "../../../translations/format-message";
import StoreAddProductManagement from "./components/add-edit-store-product";
import SortableCategoryItemProduct from "./components/sortable-category-item-product";

const StoreProductList: React.FC = (props: any) => {
  const {
    masterManagementStore,
    categoryManagementStore,
    productManagementStore,
  } = useStore();
  const {
    GetCategoryListByStoreIdForProductService,
    UpdateStoreCategoryOrderIdService,
    addUpdateStoreCategoryOrderState,
    allCategoryForProductStore,
    inProgress,
    resetAddUpdateStoreCategoryOrderState,
  } = categoryManagementStore;
  const {
    UpdateStoreProductOrderIdService,
    getProductData,
    GetProductListByCatIdService,
    addUpdateProductManagementState,
    resetAddUpdateProductManagementState,
    deleteProductManagementState,
    resetDeleteProductManagement,
    GetProductListBySearchService,
    setProductStoreFilterDetail,
    resetproductStoreFilterDetail,
    getProductStoreFilter,
    clearProductData,
    getProductDataAccordionState,
  } = productManagementStore;
  const [selectedId, setSelectedId] = useState<number>(0);
  const [freeWordSearch, setFreeWordSearch] = useState("");
  const [searchInput, setSearchInput] = useState(freeWordSearch);
  const [openAccordions, setOpenAccordions] = useState<Array<string>>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const getLocalStoreId = getStoreIdFromLocalStorage();

  // Updated state for recursive categories
  const [categories, setCategories] = useState<ICategoryProduct[]>(
    allCategoryForProductStore
  );

  const handleSearchClick = useCallback(async () => {
    const searchQuery = searchInput.trim();
    setFreeWordSearch(searchQuery);

    if (searchQuery.length === 0) {
      setOpenAccordions([]);
      resetproductStoreFilterDetail();
      setFreeWordSearch("");
      setSearchInput("");
      return;
    }

    setProductStoreFilterDetail({
      IsFilterActive: true,
      Search: searchQuery,
    });

    try {
      // Clear previous search results immediately
      setCategories((prev) => resetCategoriesProducts(prev));

      await GetProductListBySearchService(searchQuery);

      // Force UI update with new product data
      setCategories((prev) => {
        if (getProductData.size === 0) {
          setOpenAccordions([]);
          return [];
        }

        const updateCategories = (
          cats: ICategoryProduct[]
        ): ICategoryProduct[] =>
          cats.map((cat) => ({
            ...cat,
            products: getProductData.get(Number(cat.categoryId)) || [],
            subCategories: updateCategories(cat.subCategories),
          }));

        const finalUpdatedCategories = updateCategories(
          allCategoryForProductStore
        );
        setOpenAccordions(getDefaultActiveKeysProduct(finalUpdatedCategories));
        return finalUpdatedCategories;
      });
    } catch (error) {
      toast.error(formatMessage("search_failed"));
    }
  }, [
    GetProductListBySearchService,
    allCategoryForProductStore,
    getProductData,
    resetproductStoreFilterDetail,
    searchInput,
    setProductStoreFilterDetail,
  ]);

  // Transform the flat category data into a hierarchical structure when received from API
  useEffect(() => {
    // Transform the flat category data into a hierarchical structure
    setCategories(allCategoryForProductStore);
  }, [allCategoryForProductStore]);

  /**
   * This function calls the "getAllGroup" store funtion that get configuration list from API
   */
  const callGetProductManagementListService = useCallback(() => {
    GetCategoryListByStoreIdForProductService(2, getLocalStoreId);
  }, [GetCategoryListByStoreIdForProductService, getLocalStoreId]);

  useEffect(() => {
    callGetProductManagementListService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callGetProductManagementListService]);

  // useEffect(() => {
  //   if (islanguageChanged) {
  //     callGetProductManagementListService();
  //     updateLanguageChangedFlag();
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [islanguageChanged]);

  const findCategoryById = useCallback(
    (categories: ICategoryProduct[], id: string): ICategoryProduct | null => {
      for (const category of categories) {
        if (category.categoryId === id) return category;
        if (category.subCategories && category.subCategories.length > 0) {
          const found = findCategoryById(category.subCategories, id);
          if (found) return found;
        }
      }
      return null;
    },
    []
  );

  const fetchProducts = useCallback(async () => {
    if (!getProductStoreFilter.IsFilterActive) {
      if (openAccordions.length === 0) return;

      setIsRefreshing(true);
      const fetchPromises = Array.from(openAccordions).map(async (eventKey) => {
        const [, index] = eventKey.split("-");
        const categoryIndex = Number(index);
        // Make sure category exists at this index
        if (categories.length > categoryIndex) {
          const categoryId = categories[categoryIndex].categoryId;
          try {
            await GetProductListByCatIdService(
              Number(categoryId),
              getLocalStoreId,
              false
            );
            const products = getProductData.get(Number(categoryId)) || [];
            return { categoryId, products };
          } catch (error) {
            console.error(
              `Error fetching products for category ${categoryId}:`,
              error
            );
          }
        }
        return null;
      });

      const results = await Promise.all(fetchPromises);
      setCategories((prevCategories) => {
        const updated = [...prevCategories];
        results.forEach((result) => {
          if (result) {
            const category = findCategoryById(updated, result.categoryId);
            if (category) {
              category.products = result.products;
            }
          }
        });
        return updated;
      });
      setIsRefreshing(false);
    }
  }, [
    getProductStoreFilter.IsFilterActive,
    openAccordions,
    categories,
    GetProductListByCatIdService,
    getLocalStoreId,
    getProductData,
    findCategoryById,
  ]);

  const refreshProductsForOpenAccordions = useCallback(async () => {
    if (!getProductStoreFilter.IsFilterActive) {
      if (openAccordions.length === 0) return;

      setIsRefreshing(true);

      const fetchPromises = Array.from(openAccordions).map(async (eventKey) => {
        const [parentCatId, index, catId] = eventKey.split("-");
        const categoryIndex = Number(index);

        // if (categories.length > categoryIndex) {
        //   const categoryId = categories[categoryIndex].categoryId;
        const categoryId = catId;
        try {
          await GetProductListByCatIdService(
            Number(categoryId),
            getLocalStoreId,
            false
          );
          const products = getProductData.get(Number(categoryId)) || [];
          return { categoryId, products };
        } catch (error) {
          console.error(
            `Error refreshing products for category ${categoryId}:`,
            error
          );
          return { categoryId, products: [] }; // Return empty array on error
        }
        // }
        // return null;
      });

      const results = await Promise.all(fetchPromises);

      setCategories((prevCategories) => {
        const updatedCategories = [...prevCategories];

        const traverseUpdatedCategories: (
          categories: ICategoryProduct[]
        ) => ICategoryProduct[] = (categories: ICategoryProduct[]) => {
          return categories.map((cat: ICategoryProduct) => {
            const result = results.find(
              (r) => r?.categoryId === cat.categoryId
            );
            // if (result) {
            //   return { ...cat, products: result.products };
            // }
            cat.products = result?.products || [];
            if (cat.subCategories.length > 0) {
              return {
                ...cat,
                subCategories: traverseUpdatedCategories(cat.subCategories),
              };
            }
            return cat;
          });
        };
        // results.forEach((result) => {
        //   if (result) {
        //     const categoryIndex = updatedCategories.findIndex(
        //       (cat) => cat.categoryId === result.categoryId
        //     );

        //     if (categoryIndex !== -1) {
        //       updatedCategories[categoryIndex] = {
        //         ...updatedCategories[categoryIndex],
        //         products: result.products,
        //       };
        //     }
        //   }
        // });

        return traverseUpdatedCategories(updatedCategories);
      });

      setIsRefreshing(false);
    }
  }, [
    GetProductListByCatIdService,
    getLocalStoreId,
    getProductData,
    getProductStoreFilter.IsFilterActive,
    openAccordions,
  ]);

  useEffect(() => {
    if (addUpdateProductManagementState.success) {
      refreshProductsForOpenAccordions().then(() => {
        resetAddUpdateProductManagementState();
        // toast.success(formatMessage("product_updated_successfully"));
      });
    }
  }, [
    addUpdateProductManagementState.success,
    refreshProductsForOpenAccordions,
    resetAddUpdateProductManagementState,
  ]);

  const findCategoryInSubcategories = useCallback(
    (subCategories: ICategoryProduct[], productId: number): string | null => {
      for (const subCategory of subCategories) {
        if (subCategory.products?.some((t) => t.Id === productId)) {
          return subCategory.categoryId;
        }
        const found = findCategoryInSubcategories(
          subCategory.subCategories,
          productId
        );
        if (found) return found;
      }
      return null;
    },
    []
  );

  const findCategoryForProduct = useCallback(
    (productId: number): string | null => {
      for (const category of categories) {
        if (category.products?.some((t) => t.Id === productId)) {
          return category.categoryId;
        }
        // Check subcategories recursively
        const foundInSubcategory = findCategoryInSubcategories(
          category.subCategories,
          productId
        );
        if (foundInSubcategory) return foundInSubcategory;
      }
      return null;
    },
    [categories, findCategoryInSubcategories]
  );

  // For delete success
  useEffect(() => {
    if (deleteProductManagementState.success) {
      if (getProductStoreFilter.IsFilterActive) {
        setOpenAccordions([]);
        resetproductStoreFilterDetail();
        setFreeWordSearch("");
        setSearchInput("");
      }
      refreshProductsForOpenAccordions().then(() => {
        // Clear the product data for the category in the store
        if (selectedId > 0) {
          const categoryId = findCategoryForProduct(selectedId);
          if (categoryId) {
            getProductData.delete(Number(categoryId));
          }
        }
        resetDeleteProductManagement();
      });
    }
  }, [
    deleteProductManagementState.success,
    findCategoryForProduct,
    getProductData,
    getProductStoreFilter.IsFilterActive,
    refreshProductsForOpenAccordions,
    resetDeleteProductManagement,
    resetproductStoreFilterDetail,
    selectedId,
  ]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateGroupstate.success" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateStoreCategoryOrderState.success) {
      // Don't call callGetProductManagementListService here as it will reset categories
      // Just refresh products for currently open accordions
      setTimeout(() => {
        refreshProductsForOpenAccordions();
      }, 10);
      resetAddUpdateStoreCategoryOrderState();
    }
  }, [
    addUpdateStoreCategoryOrderState.success,
    refreshProductsForOpenAccordions,
    resetAddUpdateStoreCategoryOrderState,
  ]);

  const openAddProductManagementHandler = (row: { Id: number }) => {
    setSelectedId(row.Id);
    props.addToggleHandler();
    if (getProductStoreFilter.IsFilterActive) {
      setOpenAccordions([]);
      resetproductStoreFilterDetail();
      setFreeWordSearch("");
      setSearchInput("");
    }
  };

  const moveCategory = useCallback(
    (
      parentId: string | null,
      dragIndex: number,
      hoverIndex: number,
      dragCategoryId: number,
      hoverCategoryId: number
    ) => {
      const newCategories = [...categories];

      // Store open accordion category IDs (not indices)
      const openCategoryIds = openAccordions.map((key) => {
        const parts = key.split("-");
        return parts[parts.length - 1]; // Get the category ID from the key
      });

      // If it's a root category
      if (parentId === null) {
        const [movedCategory] = newCategories.splice(dragIndex, 1);
        newCategories.splice(hoverIndex, 0, movedCategory);

        let draggedCategory = newCategories
          .map((cat) => ({
            ...cat,
            ParentCategoryId: 0
          }))
          .filter(
            (cat) =>
              Number(cat.categoryId) === dragCategoryId
          );
        let droppedCategory = newCategories
          .map((cat) => ({
            ...cat,
            ParentCategoryId: 0
          }))
          .filter(
            (cat) =>
              Number(cat.categoryId) === hoverCategoryId
          );

        // Update categories state immediately with treatments preserved
        setCategories(newCategories);

        // Rebuild open accordions with new indices
        const newOpenAccordions: string[] = [];
        newCategories.forEach((cat, idx) => {
          if (openCategoryIds.includes(cat.categoryId)) {
            newOpenAccordions.push(`root-${idx}-${cat.categoryId}`);
          }
        });
        setOpenAccordions(newOpenAccordions);

        UpdateStoreCategoryOrderIdService([...droppedCategory, ...draggedCategory], getLocalStoreId, 2);
      } else {
        // For nested categories, we need to find the parent
        const findAndUpdateParent = (
          categories: ICategoryProduct[]
        ): ICategoryProduct[] => {
          return categories.map((category) => {
            if (category.categoryId === parentId) {
              // Found the parent, update its subcategories
              const newSubCategories = [...category.subCategories];
              const [movedCategory] = newSubCategories.splice(dragIndex, 1);
              newSubCategories.splice(hoverIndex, 0, movedCategory);

              return {
                ...category,
                subCategories: newSubCategories,
              };
            }

            if (category.subCategories.length > 0) {
              return {
                ...category,
                subCategories: findAndUpdateParent(category.subCategories),
              };
            }

            return category;
          });
        };

        let droppedSubCategory = findAndUpdateParent(newCategories);

        // Update categories state with treatments preserved
        setCategories(droppedSubCategory);

        let draggedCategory = findCategoryById(
          droppedSubCategory,
          String(dragCategoryId)
        );
        let hoveredCategory = findCategoryById(
          droppedSubCategory,
          String(hoverCategoryId)
        );

        // let draggedCatOrderId = draggedCategory!.orderId;
        // draggedCategory!.orderId = hoveredCategory!.orderId;
        // hoveredCategory!.orderId = draggedCatOrderId;

        let finalDroppedSubCategory = [{...hoveredCategory, ParentCategoryId: parentId}, {...draggedCategory, ParentCategoryId: parentId}];

        // Rebuild open accordions for subcategories
        const rebuildAccordionKeys = (
          cats: ICategoryProduct[],
          parentKey: string = "root"
        ): void => {
          cats.forEach((cat, idx) => {
            const currentKey = `${parentKey}-${idx}-${cat.categoryId}`;
            if (openCategoryIds.includes(cat.categoryId)) {
              const keyIndex = openAccordions.findIndex((k) =>
                k.endsWith(`-${cat.categoryId}`)
              );
              if (keyIndex !== -1) {
                openAccordions[keyIndex] = currentKey;
              }
            }
            if (cat.subCategories.length > 0) {
              rebuildAccordionKeys(cat.subCategories, currentKey);
            }
          });
        };

        rebuildAccordionKeys(droppedSubCategory);
        setOpenAccordions([...openAccordions]);

        UpdateStoreCategoryOrderIdService(
          finalDroppedSubCategory,
          getLocalStoreId,
          2
        );
      }
    },
    [
      UpdateStoreCategoryOrderIdService,
      categories,
      getLocalStoreId,
      openAccordions,
      setOpenAccordions,
      findCategoryById,
      setCategories,
    ]
  );

  const moveItem = useCallback(
    (
      dragIndex: number,
      hoverIndex: number,
      sourceCategoryId: string,
      targetCategoryId: string,
      dragProductId: number,
      hoverProductId: number
    ) => {
      if (sourceCategoryId !== targetCategoryId) return;

      const prevCategories = [...categories];
      const updateCategoryProducts = (
        category: ICategoryProduct[]
      ): ICategoryProduct[] => {
        return category.map((cat) => {
          if (cat.categoryId === sourceCategoryId) {
            // Create new array with moved product
            const newProducts = [...(cat.products || [])];
            const [movedItem] = newProducts.splice(dragIndex, 1);
            newProducts.splice(hoverIndex, 0, movedItem);
            return { ...cat, products: newProducts };
          }
          if (cat.subCategories.length > 0) {
            return {
              ...cat,
              subCategories: updateCategoryProducts(cat.subCategories),
            };
          }
          return cat;
        });
      };

      let updatedProducts = updateCategoryProducts([...prevCategories]);
      let finalDraggedProduct = [];
      let finalDroppedProduct = [];
      // Find the category (could be at any level)
      const sourceCategory = findCategoryById(
        updatedProducts,
        sourceCategoryId
      );

      if (sourceCategory) {
        finalDraggedProduct = sourceCategory.products
          .map((product) => ({
            ...product,
            CategoryId: Number(sourceCategoryId),
          }))
          .filter(
            (product) =>
              Number(product.Id) === dragProductId
          );
        finalDroppedProduct = sourceCategory.products
          .map((product) => ({
            ...product,
            CategoryId: Number(sourceCategoryId),
          }))
          .filter(
            (product) =>
              Number(product.Id) === hoverProductId
          );
        UpdateStoreProductOrderIdService([...finalDroppedProduct, ...finalDraggedProduct], getLocalStoreId);
      }
    },
    [
      categories,
      findCategoryById,
      UpdateStoreProductOrderIdService,
      getLocalStoreId,
    ]
  );


  const handleDetailClick = (product: any) => {
    openAddProductManagementHandler({ Id: product.Id });
  };

  useEffect(() => {
    if (searchInput === "" && getProductStoreFilter.IsFilterActive) {
      resetproductStoreFilterDetail();
      setOpenAccordions([]);
      callGetProductManagementListService();
    }
  }, [
    callGetProductManagementListService,
    getProductStoreFilter.IsFilterActive,
    resetproductStoreFilterDetail,
    searchInput,
  ]);

  return (
    <React.Fragment>
      <FormLoader
        loading={
          addUpdateStoreCategoryOrderState.inProgress ||
          inProgress ||
          isRefreshing
        }
      />

      {props.showAddPopup && (
        <StoreAddProductManagement
          id={selectedId}
          modalClosed={props.addToggleHandler}
          storeId={getLocalStoreId}
        />
      )}
      {props.showDeletePopup && (
        <DeletePopup
          title="delete_User"
          modalSubmit={() => {}}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_user_confirm"
          isLoading={false}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="product_management" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="store_management" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="product_management" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0">
        <div className="row mb-2">
          <div className="col-6 search_box">
            <div className="row mb-3">
              <div className="col-xl-4 col-sm-8 col-12">
                <div className="input-group">
                  <div className="input-group-text">
                    <FontAwesomeIcon
                      icon={faSearch}
                      onClick={handleSearchClick}
                    />
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("search_by_free_word")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearchClick();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-6">
            <div className="d-flex justify-content-end align-items-center">
              {getProductStoreFilter.IsFilterActive && (
                <button
                  className={`btn ${
                    getProductStoreFilter.IsFilterActive
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => {
                    resetproductStoreFilterDetail();
                    setFreeWordSearch("");
                    setSearchInput("");
                    setOpenAccordions([]);
                    callGetProductManagementListService();
                  }}
                  title={formatMessage(
                    getProductStoreFilter.IsFilterActive
                      ? "clear_filter"
                      : "apply_filter"
                  )}
                >
                  <svg
                    id="fi_3126668"
                    enableBackground="new 0 0 512 512"
                    height="18"
                    viewBox="0 0 512 512"
                    width="18"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#ffffff"
                  >
                    <g>
                      <path d="m215.546 85.327h-162.264c-18.073 0-28.679 20.379-18.31 35.187.133.199-3.448-4.682 130.024 177.006 5.921 8.587 4.149-.599 4.149 190.987 0 19.245 21.993 30.358 37.542 18.791 57.536-43.372 71.516-48.257 71.516-70.955 0-133.909-1.721-130.311 4.149-138.823l27.851-37.923c-70.082-25.496-112.087-99.608-94.657-174.27z"></path>
                      <path d="m281.951 30.166c-75.076 67.31-38.685 187.35 55.962 206.05 75.479 15.948 143.193-43.867 143.193-116.945 0-102.594-122.364-157.159-199.155-89.105zm118.529 106.804c9.515 9.466 2.715 25.676-10.603 25.676-8.014 0-10.022-3.79-28.462-22.158-18.064 17.984-20.27 22.158-28.472 22.158-13.349 0-20.063-16.264-10.603-25.676l17.769-17.699-17.769-17.699c-14.107-14.035 7.142-35.322 21.216-21.297l17.859 17.779 17.849-17.779c14.074-14.025 35.331 7.254 21.216 21.297l-17.769 17.699z"></path>
                    </g>
                  </svg>
                </button>
              )}
              {/* 
              ADDED REFRESH BUTTON FOR TESTING WILL REMOVE IT AFTER FINAL COMMIT , FOR NOW COMMITING IT FOR FUTURE ID MISMATCH ISSUE
              {openAccordions.size > 0 && (
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  {formatMessage("refresh")}
                </button>
              )} */}
              <button
                className="btn btn-primary ms-3"
                onClick={() => {
                  openAddProductManagementHandler({ Id: -1 });
                }}
              >
                <i className="bi bi-plus-lg me-1"></i>
                {formatMessage("add_product")}
              </button>
            </div>
          </div>
        </div>
        {categories.length > 0 ? (
          <>
            {categories?.map((category, index) => {
              return (
                // <Accordion alwaysOpen defaultActiveKey={[`root-0`, `root-1`]}>
                <SortableCategoryItemProduct
                  key={category.categoryId}
                  category={category}
                  index={index}
                  parentId={null}
                  moveCategory={moveCategory}
                  moveItem={moveItem}
                  onDetailClick={handleDetailClick}
                  setCategories={setCategories}
                  storeId={getLocalStoreId}
                  openAccordions={openAccordions}
                  setOpenAccordions={setOpenAccordions}
                />
              );
            })}
          </>
        ) : (
          <div className="message text-center">
            <img src={noMessageIcon} alt="" className="no_message" />
            {freeWordSearch.trim().length > 0 ? (
              <>
                <p className="noData text-muted text-center h4 mb-0">
                  <FormattedMessage id="no_data_available" />
                </p>
              </>
            ) : (
              <>
                {getLocalStoreId > 0 ? (
                  <>
                    <p className="noData text-muted text-center h4 mb-0">
                      <FormattedMessage id="no_product" />
                    </p>
                    <p className="mb-3">
                      {formatMessage("no_product_message")}
                    </p>
                  </>
                ) : (
                  <p className="mb-3">{formatMessage("please_select_store")}</p>
                )}
                <LoaderButton
                  text="add_product"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                    openAddProductManagementHandler({ Id: -1 })
                  }
                />
              </>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddPopupHOC(
  ShowPopupHOC(DeletePopupHOC(observer(StoreProductList)))
);
